/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/controller/useInboxMutationActions.ts
 * FILE_ID: EMAIL-INBOX-CONTROLLER-MUTATIONS-001
 * PURPOSE: Own row selection, disposition, star, read, and bulk mutations.
 */

/* eslint-disable react-hooks/exhaustive-deps -- Preserve pre-extraction hook dependencies. */

import { useCallback, useRef, useState } from 'react'
import { emitSearchTelemetry } from '@quotech/ui'
import { type Email } from '../../../hooks/useEmails'
import {
  siblingDuplicateIds,
  type DedupedEmail,
} from '../../../lib/dedup'
import { applyEmailSelection, nextSelectionAfterRemoval } from '../../inboxSelection'
import { nextNavOnRowClick } from '../../inboxRowClickNav'
import { resolveTargetAfterRemovals } from '../../../email-reader'
import type { useInboxDataRuntime } from './useInboxDataRuntime'
import type { useInboxState } from './useInboxState'

type InboxState = ReturnType<typeof useInboxState>
type InboxData = ReturnType<typeof useInboxDataRuntime>

export function useInboxMutationActions(
  state: InboxState,
  data: InboxData,
  getDedupedEmails: () => DedupedEmail[],
) {
  const handleToggleStar = async (
    email: Email,
    event: React.MouseEvent,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    data.updateEmailLocal(email.id, { is_starred: !email.is_starred })
    try {
      await data.toggleStar(email.id, email.is_starred)
      void data.syncDuplicates(
        siblingDuplicateIds(getDedupedEmails(), [email.id]),
        email.is_starred ? 'unstar' : 'star',
      )
      // BL-1 / #27: local cache already reflects the star — no full inbox refetch.
    } catch {
      data.updateEmailLocal(email.id, { is_starred: email.is_starred })
      data.toast(
        email.is_starred
          ? 'Couldn\u2019t unstar - try again.'
          : 'Couldn\u2019t star - try again.',
        'error',
      )
    }
  }
  const selectionAnchorRef = useRef<string | null>(null)
  const handleToggleSelect = (
    emailId: string,
    event: React.MouseEvent,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    const orderedIds = getDedupedEmails().map((row) => row.id)
    state.setSelectedIds((previous) => {
      const applied = applyEmailSelection({
        orderedIds,
        selected: previous,
        anchorId: selectionAnchorRef.current,
        clickedId: emailId,
        shift: event.shiftKey,
      })
      selectionAnchorRef.current = applied.anchorId
      return applied.selected
    })
  }
  const handleEmailClick = async (email: Email, event?: React.MouseEvent) => {
    if (event?.shiftKey) {
      handleToggleSelect(email.id, event)
      return
    }
    const decision = nextNavOnRowClick(state.location.pathname)
    if (decision.navigateTo) {
      state.navigate(decision.navigateTo, { replace: true })
    }
    const clickSearchQuery =
      (state.parsedDebouncedSearch.freeText || '').trim()
    if (clickSearchQuery) {
      emitSearchTelemetry({
        kind: 'result_selected',
        query: clickSearchQuery,
        index: getDedupedEmails().findIndex(
          (candidate) => candidate.id === email.id,
        ),
        resultId: email.id,
        resultType: 'email',
        surface: 'email-inbox',
      })
    }
    state.openSelectedEmailId(email.id)
    if (!email.is_read) {
      data.updateEmailLocal(email.id, { is_read: true })
      try { await data.markAsRead(email.id) } catch { /* poll self-heals */ }
      void data.syncDuplicates(
        siblingDuplicateIds(getDedupedEmails(), [email.id]),
        'mark_read',
      )
    }
  }
  const handleBulkAction = async (action: string) => {
    if (state.selectedIds.size === 0) return
    const ids = Array.from(state.selectedIds)
    const count = state.selectedIds.size
    // The bulk bar labels this "Done". Reader Done and hotkey `c` archive
    // AND pass complete:true so the linked card moves to its DONE lane.
    // A plain archive bulk call hides the mail and leaves the card open.
    const completesLinkedWork = action === 'done'
    const backendAction = completesLinkedWork
      ? 'archive'
      : action === 'trash' ? 'delete'
        : action === 'read' ? 'mark_read'
          : action === 'unread' ? 'mark_unread'
            : action
    // In the Binned folder a bulk Restore sends the rows back to the inbox,
    // so they leave THIS list the same way Done/Junk leave the inbox.
    const clears =
      backendAction === 'archive'
      || backendAction === 'delete'
      || (backendAction === 'restore' && state.folder === 'binned')
    // BUG-2026-08-03-BULK-ARCHIVE-SIBLING-AFTER-REMOVE: capture hidden
    // multi-mailbox copies BEFORE optimistic remove. After removeEmailsLocal
    // the survivors are gone from getDedupedEmails(), so siblingDuplicateIds
    // returned [] and those copies reappeared on the next sync/poll
    // (Newmark: Mark archived his list; mail "came back" ~minutes later).
    // Same contract as handleArchiveRow / handleBinRow / useInboxHotkeys.
    const visible = getDedupedEmails()
    const siblings = siblingDuplicateIds(visible, ids)
    const doneSnapshots = completesLinkedWork
      ? ids.map((id) => visible.find((email) => email.id === id))
      : []
    const previousSelection = new Set(state.selectedIds)
    const previousReaderSelection = state.selectedEmailId
    if (clears) {
      if (previousReaderSelection && previousSelection.has(previousReaderSelection)) {
        state.setSelectedEmailId(
          resolveTargetAfterRemovals(
            getDedupedEmails().map((email) => email.id),
            previousReaderSelection,
            previousSelection,
          ),
        )
      }
      data.removeEmailsLocal(ids)
    }
    state.setSelectedIds(new Set())
    try {
      if (completesLinkedWork) {
        await Promise.all(ids.map((id, index) => {
          const email = doneSnapshots[index]
          return data.archive(id, {
            folder: email?.folder,
            isRead: email?.is_read,
            isStarred: email?.is_starred,
            complete: true,
          })
        }))
      } else {
        await data.bulkAction(ids, backendAction)
      }
      void data.syncDuplicates(siblings, backendAction)
      if (clears) {
        void data.refetchStats()
      }
      // BL-1: removeEmailsLocal / updateEmailLocal already applied — skip list refetch.
    } catch {
      state.setSelectedIds(previousSelection)
      if (clears && previousReaderSelection) {
        state.setSelectedEmailId(previousReaderSelection)
      }
      const verb =
        action === 'trash'
          ? 'junk'
          : action === 'done'
            ? 'mark done'
            : action === 'read' ? 'mark read' : action
      data.toast(
        `Couldn\u2019t ${verb} ${count} email${count === 1 ? '' : 's'} - try again.`,
        'error',
      )
      data.refetch()
    }
  }
  /**
   * Reader terminal-action seam (change #269, Phase 2): remove the row and
   * advance selection NOW (same as handleEmailResolved), but hand back a
   * restore closure that reinserts the row at its ORIGINAL index and
   * restores the reader selection — the runner calls it when the server
   * write fails or the operator presses Undo.
   */
  const beginTerminalRemoval = useCallback((id: string): (() => void) | void => {
    const deduped = getDedupedEmails()
    const removedIndex = deduped.findIndex((email) => email.id === id)
    const removedEmail = removedIndex >= 0 ? deduped[removedIndex] : null
    const previousSelection = state.selectedEmailId
    if (state.selectedEmailId === id) {
      state.setSelectedEmailId(
        nextSelectionAfterRemoval(
          deduped.map((email) => email.id),
          id,
        ),
      )
    }
    data.removeEmailsLocal([id])
    void data.refetchStats()
    if (!removedEmail) return undefined
    return () => {
      data.restoreEmailsLocal([{ email: removedEmail, index: removedIndex }])
      if (previousSelection === id) state.setSelectedEmailId(id)
      void data.refetchStats()
    }
  }, [
    state.selectedEmailId,
    state.setSelectedEmailId,
    data.removeEmailsLocal,
    data.restoreEmailsLocal,
    data.refetchStats,
    getDedupedEmails,
  ])

  const handleEmailResolved = useCallback((id: string) => {
    if (state.selectedEmailId === id) {
      state.setSelectedEmailId(
        nextSelectionAfterRemoval(
          getDedupedEmails().map((email) => email.id),
          id,
        ),
      )
    }
    data.removeEmailsLocal([id])
    void data.refetchStats()
  }, [
    state.selectedEmailId,
    state.setSelectedEmailId,
    data.removeEmailsLocal,
    data.refetchStats,
    getDedupedEmails,
  ])
  const [binningId, setBinningId] = useState<string | null>(null)
  const handleBinRow = async (email: Email) => {
    setBinningId(email.id)
    const siblings = siblingDuplicateIds(getDedupedEmails(), [email.id])
    if (state.selectedEmailId === email.id) {
      state.setSelectedEmailId(
        nextSelectionAfterRemoval(
          getDedupedEmails().map((candidate) => candidate.id),
          email.id,
        ),
      )
    }
    data.removeEmailsLocal([email.id])
    let binned = false
    try {
      await data.bulkAction([email.id], 'delete')
      binned = true
      void data.syncDuplicates(siblings, 'delete')
      void data.refetchStats()
    } catch {
      data.toast('Couldn\u2019t move to Bin - try again.', 'error')
      data.refetch()
    } finally {
      setBinningId(null)
    }
    if (!binned) return
    // B-04: do NOT category-sweep "similar sender" into Trash. That path was
    // a destructive category-only bulk delete with no undo manifest. Single-
    // email bin (above) remains; mass Done/Junk belongs on the reversible
    // inbox-sweep / selection-manifest surfaces.
  }
  const [archivingId, setArchivingId] = useState<string | null>(null)
  const handleArchiveRow = async (email: Email) => {
    setArchivingId(email.id)
    const siblings = siblingDuplicateIds(getDedupedEmails(), [email.id])
    if (state.selectedEmailId === email.id) {
      state.setSelectedEmailId(
        nextSelectionAfterRemoval(
          getDedupedEmails().map((candidate) => candidate.id),
          email.id,
        ),
      )
    }
    data.removeEmailsLocal([email.id])
    try {
      await data.archive(email.id, {
        folder: email.folder,
        isRead: email.is_read,
        isStarred: email.is_starred,
      })
      void data.syncDuplicates(siblings, 'archive')
      void data.refetchStats()
    } catch {
      data.toast('Couldn\u2019t archive - try again.', 'error')
      data.refetch()
    } finally {
      setArchivingId(null)
    }
  }
  const [unarchivingId, setUnarchivingId] = useState<string | null>(null)
  const handleUnarchiveRow = async (email: Email) => {
    setUnarchivingId(email.id)
    // In the Archived folder, restoring removes the row from this list.
    // In Inbox "Everything" (include_archived), keep the row and flip the flag.
    const leaveList = state.folder === 'archived'
    const siblings = siblingDuplicateIds(getDedupedEmails(), [email.id])
    if (leaveList) {
      if (state.selectedEmailId === email.id) {
        state.setSelectedEmailId(
          nextSelectionAfterRemoval(
            getDedupedEmails().map((candidate) => candidate.id),
            email.id,
          ),
        )
      }
      data.removeEmailsLocal([email.id])
    } else {
      data.updateEmailLocal(email.id, { is_archived: false })
    }
    try {
      await data.unarchive(email.id, { isRead: email.is_read })
      void data.syncDuplicates(siblings, 'unarchive')
      void data.refetchStats()
    } catch {
      if (!leaveList) data.updateEmailLocal(email.id, { is_archived: true })
      data.toast('Couldn\u2019t unarchive - try again.', 'error')
      data.refetch()
    } finally {
      setUnarchivingId(null)
    }
  }

  const [restoringId, setRestoringId] = useState<string | null>(null)
  const handleRestoreRow = async (email: Email) => {
    setRestoringId(email.id)
    const siblings = siblingDuplicateIds(getDedupedEmails(), [email.id])
    if (state.selectedEmailId === email.id) {
      state.setSelectedEmailId(
        nextSelectionAfterRemoval(
          getDedupedEmails().map((candidate) => candidate.id),
          email.id,
        ),
      )
    }
    data.removeEmailsLocal([email.id])
    try {
      await data.bulkAction([email.id], 'restore')
      void data.syncDuplicates(siblings, 'restore')
      void data.refetchStats()
    } catch {
      data.toast('Couldn\u2019t restore - try again.', 'error')
      data.refetch()
    } finally {
      setRestoringId(null)
    }
  }

  const [readTogglingId, setReadTogglingId] = useState<string | null>(null)
  const handleToggleReadRow = async (email: Email) => {
    setReadTogglingId(email.id)
    data.updateEmailLocal(email.id, { is_read: !email.is_read })
    try {
      await data.toggleRead(email.id, email.is_read)
      void data.syncDuplicates(
        siblingDuplicateIds(getDedupedEmails(), [email.id]),
        email.is_read ? 'mark_unread' : 'mark_read',
      )
    } catch {
      data.updateEmailLocal(email.id, { is_read: email.is_read })
      data.toast(
        email.is_read
          ? 'Couldn\u2019t mark unread - try again.'
          : 'Couldn\u2019t mark read - try again.',
        'error',
      )
    } finally {
      setReadTogglingId(null)
    }
  }

  return {
    handleToggleStar, handleEmailClick, handleToggleSelect, handleBulkAction,
    handleEmailResolved, beginTerminalRemoval, binningId, handleBinRow,
    archivingId, handleArchiveRow, unarchivingId, handleUnarchiveRow,
    restoringId, handleRestoreRow, readTogglingId, handleToggleReadRow,
  }
}
